import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { usePatientOrderTracking } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { PatientOrderTrackingFilters } from '../services/reportsService';

interface PatientOrderTrackingSectionProps {
  locationIds?: string[];
}

export function PatientOrderTrackingSection({
  locationIds,
}: PatientOrderTrackingSectionProps) {
  const [patientNameFilter, setPatientNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    ('PENDING' | 'ORDERED' | 'RECEIVED')[]
  >(['PENDING', 'ORDERED', 'RECEIVED']);

  const filters: PatientOrderTrackingFilters = {
    location_ids: locationIds,
    patient_name: patientNameFilter || undefined,
    status: statusFilter.length < 3 ? statusFilter : undefined,
  };

  const { data, isLoading, error } = usePatientOrderTracking(filters);

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        'Patient Name': item.patient_name,
        'Tray Number': item.tray_number,
        Location: item.location_name,
        Manufacturer: item.manufacturer_name,
        Brand: item.brand,
        'Frame Model': item.frame_name_or_model,
        Color: item.color,
        Size: item.eye_size,
        Status: item.status,
        'Entry Date': item.reorder_entry_date,
        'Ordered Date': item.ordered_date || '',
        'Received Date': item.received_date || '',
        'Days Since Entry': item.days_since_entry,
        'Days Since Ordered': item.days_since_ordered || '',
        'PO Number': item.po_number || '',
      })),
      'patient-order-tracking-report'
    );
  };

  const getStatusBadgeVariant = (
    status: 'PENDING' | 'ORDERED' | 'RECEIVED'
  ) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ORDERED':
        return 'info';
      case 'RECEIVED':
        return 'success';
    }
  };

  const toggleStatusFilter = (status: 'PENDING' | 'ORDERED' | 'RECEIVED') => {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Patient Order Tracking</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Patient Order Tracking</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  const pendingCount = data?.filter((item) => item.status === 'PENDING').length || 0;
  const orderedCount = data?.filter((item) => item.status === 'ORDERED').length || 0;
  const receivedCount = data?.filter((item) => item.status === 'RECEIVED').length || 0;

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Patient Order Tracking</h2>
          <p className="text-sm text-gray-500 mt-1">
            Track all patient-specific frame orders
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Patient Name
          </label>
          <Input
            type="text"
            value={patientNameFilter}
            onChange={(e) => setPatientNameFilter(e.target.value)}
            placeholder="Enter patient name..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status Filter
          </label>
          <div className="flex gap-2">
            {(['PENDING', 'ORDERED', 'RECEIVED'] as const).map((status) => (
              <Button
                key={status}
                variant={statusFilter.includes(status) ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => toggleStatusFilter(status)}
              >
                {status}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-2 border-yellow-200 bg-yellow-50">
          <div className="text-sm text-gray-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
        </Card>
        <Card className="p-4 border-2 border-blue-200 bg-blue-50">
          <div className="text-sm text-gray-600">Ordered</div>
          <div className="text-2xl font-bold text-blue-600">{orderedCount}</div>
        </Card>
        <Card className="p-4 border-2 border-green-200 bg-green-50">
          <div className="text-sm text-gray-600">Received</div>
          <div className="text-2xl font-bold text-green-600">{receivedCount}</div>
        </Card>
      </div>

      {!data || data.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No patient orders found matching the filters
        </div>
      ) : (
        <>
          {/* Patient Orders Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Patient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Frame Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Entry Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Days Since Entry
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    PO#
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium">{item.patient_name}</div>
                      <div className="text-sm text-gray-500">Tray {item.tray_number}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className="hidden md:inline">{item.location_name}</span>
                      <span className="md:hidden">
                        {item.location_short_code || item.location_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="max-w-xs">
                        <div className="font-medium">
                          {item.brand} - {item.frame_name_or_model}
                        </div>
                        <div className="text-gray-500">
                          {item.color} | Size {item.eye_size}
                        </div>
                        <div className="text-xs text-gray-400">
                          {item.manufacturer_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={getStatusBadgeVariant(item.status)}>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {formatDate(item.reorder_entry_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span
                        className={
                          item.days_since_entry <= 7
                            ? 'text-green-600'
                            : item.days_since_entry <= 14
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }
                      >
                        {item.days_since_entry} days
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.po_number || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            Showing {data.length} patient order{data.length !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </Card>
  );
}


